import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import RsvpButtons from './RsvpButtons';
import { PostCard } from '../posts/PostCard';
import { PostComposer } from '../posts/PostComposer';
import type { Event } from '../../services/events';
import { getFeed } from '../../services/posts';
import { useAuth } from '../../hooks/useAuth';

interface EventCardProps {
  event: Event;
  expanded?: boolean;
  currentUserId?: string;
}

export default function EventCard({ event, expanded, currentUserId }: EventCardProps) {
  const [showComposer, setShowComposer] = useState(false);
  const { user } = useAuth();
  const startDate = new Date(event.startTime);
  const organizerUsers = (event.organizers?.map((o) => o.user) ?? (event.organizer ? [event.organizer] : [])).filter(Boolean);
  const currentUserRsvp = currentUserId
    ? (event.attendees ?? []).find((a) => a.user.id === currentUserId)?.status ?? null
    : null;
  const isOrganizer = organizerUsers.some((o) => o.id === user?.id)
    || (event.organizerId != null && event.organizerId === user?.id);
  const canManage = isOrganizer || Boolean(user?.isAdmin);

  // Fetch comments (posts linked to this event) only when expanded
  const { data: postsData, isLoading: postsLoading } = useInfiniteQuery({
    queryKey: ['posts', 'event', event.id],
    queryFn: ({ pageParam = 1 }) => getFeed(pageParam, 20, event.id),
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.page + 1 : undefined,
    initialPageParam: 1,
    enabled: !!expanded,
  });

  const comments = postsData?.pages.flatMap((p) => p.posts) ?? [];

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('da-DK', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('da-DK', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const inner = (
    <>
      {event.imageUrl && (
        <img
          src={event.imageUrl}
          alt={event.title}
          className="w-full h-48 object-cover rounded-image"
        />
      )}

      <div className="p-4">
        {/* Date badge */}
        <div className="flex items-center gap-2 mb-3">
          <div className="bg-orange-400 text-white px-3 py-1 rounded-full text-sm font-semibold">
            {isToday(startDate) ? 'I DAG' : formatDate(startDate)}
          </div>
        </div>

        {/* Title — always a link to detail page */}
        {expanded ? (
          <div className="flex items-start justify-between gap-2 mb-2">
            <Link to={`/events/${event.id}`} className="no-underline hover:no-underline flex-1">
              <h3 className="text-xl font-bold text-gray-900 hover:text-primary-600 transition-colors">{event.title}</h3>
            </Link>
            {canManage && (
              <Link
                to={`/events/${event.id}/edit`}
                className="flex-shrink-0 px-3 py-1 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Rediger
              </Link>
            )}
          </div>
        ) : (
          <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
        )}

        {/* Time */}
        <div className="flex items-center gap-2 text-gray-600 mb-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>
            {formatTime(startDate)}
            {(() => {
              if (!event.endTime) return null;
              const endDate = new Date(event.endTime);
              const defaultEnd = new Date(startDate.getTime() + 3 * 60 * 60 * 1000);
              if (Math.abs(endDate.getTime() - defaultEnd.getTime()) < 60000) return null;
              return <> &ndash; {formatTime(endDate)}</>;
            })()}
          </span>
        </div>

        {/* Location */}
        {event.location && (
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>{event.location}</span>
          </div>
        )}

        {/* Organizer */}
        <div className="text-sm text-gray-500 mb-3">
          {organizerUsers.length > 0
            ? `Arrangeret af ${organizerUsers.map((o) => o.name).join(', ')}`
            : 'Arrangeret af slettet bruger'}
        </div>

        {/* Description */}
        <p className={`text-gray-700 mb-3 whitespace-pre-wrap ${expanded ? '' : 'line-clamp-2'}`}>{event.description}</p>

        {/* RSVP Lists */}
        <div className="space-y-2 mb-3 text-sm">
          {(event.attendees ?? []).filter(a => a.status === 'attending').length > 0 && (
            <div className="flex items-start gap-2">
              <span className="text-primary-600 font-medium min-w-[80px]">Deltager ({(event.attendees ?? []).filter(a => a.status === 'attending').length}):</span>
              <span className="text-gray-700 flex-1">
                {(event.attendees ?? []).filter(a => a.status === 'attending').map(a => a.user.name).join(', ')}
              </span>
            </div>
          )}
          {(event.attendees ?? []).filter(a => a.status === 'maybe').length > 0 && (
            <div className="flex items-start gap-2">
              <span className="text-yellow-600 font-medium min-w-[80px]">Måske ({(event.attendees ?? []).filter(a => a.status === 'maybe').length}):</span>
              <span className="text-gray-700 flex-1">
                {(event.attendees ?? []).filter(a => a.status === 'maybe').map(a => a.user.name).join(', ')}
              </span>
            </div>
          )}
          {(event.attendees ?? []).filter(a => a.status === 'not_attending').length > 0 && (
            <div className="flex items-start gap-2">
              <span className="text-gray-500 font-medium min-w-[80px]">Afbud ({(event.attendees ?? []).filter(a => a.status === 'not_attending').length}):</span>
              <span className="text-gray-600 flex-1">
                {(event.attendees ?? []).filter(a => a.status === 'not_attending').map(a => a.user.name).join(', ')}
              </span>
            </div>
          )}
          {event.noResponse && event.noResponse.length > 0 && (
            <div className="flex items-start gap-2">
              <span className="text-orange-600 font-medium min-w-[80px]">Mangler svar ({event.noResponse.length}):</span>
              <span className="text-gray-600 flex-1">
                {event.noResponse.map(u => u.name).join(', ')}
              </span>
            </div>
          )}
        </div>

        {/* RSVP Buttons */}
        {expanded && (
          <div className="pt-3 border-t border-gray-100 mt-3">
            <RsvpButtons eventId={event.id} currentStatus={currentUserRsvp} />
          </div>
        )}

        {/* Inline comments section */}
        {expanded && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-800">
                Kommentarer{comments.length > 0 ? ` (${comments.length})` : ''}
              </h4>
              <button
                onClick={() => setShowComposer((v) => !v)}
                className="text-sm px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                {showComposer ? 'Luk' : 'Skriv kommentar'}
              </button>
            </div>

            {showComposer && (
              <div className="mb-3">
                <PostComposer eventId={event.id} onPostCreated={() => setShowComposer(false)} />
              </div>
            )}

            {postsLoading ? (
              <div className="space-y-2">
                {[1, 2].map((i) => (
                  <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
                ))}
              </div>
            ) : comments.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-3">Ingen kommentarer endnu</p>
            ) : (
              <div className="space-y-3">
                {comments.map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );

  if (expanded) {
    return <div className="card bg-white shadow hover:shadow-lg transition-shadow">{inner}</div>;
  }

  return (
    <Link to={`/events/${event.id}`} className="card block hover:shadow-lg no-underline">
      {inner}
    </Link>
  );
}

